// src/assets/Js/auth.js
// =====================================================
// HttpOnly Cookie 登入狀態管理
// -----------------------------------------------------
// 重點：
// 1. 前端不能讀取 HttpOnly Cookie 裡的 token。
// 2. 前端要透過 /auth/status 問後端目前是否已登入。
// 3. 後端會從 Cookie 驗證 JWT，並回傳可安全給前端使用的使用者資訊。
// =====================================================

const AUTH_STATUS_STORAGE_KEY = "orderfoodpos.authStatus";

let memoryAuthStatus = null;
let pendingAuthStatusRequest = null;

// =====================================================
// 正規化後端回傳的登入資訊
// -----------------------------------------------------
// 後端只回傳目前登入者可安全使用的資訊，不包含內部資料庫 StoreId。
// 這裡轉成前端比較容易使用的格式，並補上舊程式常用的 fullName / admin。
// =====================================================
function normalizeAuthStatus(data = {}) {
  const role = data.role || "";
  const fullName = data.fullName || data.fullname || data.name || "";
  const storePublicId =
    data.storePublicId ?? data.StorePublicId ?? data.publicId ?? null;

  return {
    authenticated: data.authenticated === true,
    username: data.username || "",
    name: data.name || fullName,
    fullname: fullName,
    fullName,
    email: data.email || "",
    role,
    admin: role === "Admin" || data.admin === true || data.admin === "true",
    storePublicId: storePublicId ? String(storePublicId) : null,
    storeName: data.storeName || "",
    type: data.type || "",
  };
}

// =====================================================
// 寫入登入狀態快取
// -----------------------------------------------------
// 這裡只存後端允許給前端看的資訊，不存 JWT token。
// =====================================================
export function setAuthStatus(data) {
  const status = normalizeAuthStatus(data);
  memoryAuthStatus = status;

  try {
    if (status.authenticated) {
      sessionStorage.setItem(AUTH_STATUS_STORAGE_KEY, JSON.stringify(status));
    } else {
      sessionStorage.removeItem(AUTH_STATUS_STORAGE_KEY);
    }
  } catch (error) {
    console.warn("[auth] 寫入登入狀態快取失敗", error);
  }

  return status;
}

// =====================================================
// 取得登入狀態快取
// -----------------------------------------------------
// 用於舊程式仍需要同步取得 role、帳號與公開店家識別碼的地方。
// 真正的登入有效性仍以 /auth/status 為準。
// =====================================================
export function getCachedAuthStatus() {
  if (memoryAuthStatus) return memoryAuthStatus;

  try {
    const cached = sessionStorage.getItem(AUTH_STATUS_STORAGE_KEY);
    if (!cached) return null;

    memoryAuthStatus = normalizeAuthStatus(JSON.parse(cached));
    return memoryAuthStatus;
  } catch (error) {
    console.warn("[auth] 讀取登入狀態快取失敗", error);
    return null;
  }
}

// =====================================================
// 清除登入狀態快取
// -----------------------------------------------------
// 登出、401、或登入失效時呼叫。
// =====================================================
export function clearAuthStatus() {
  memoryAuthStatus = null;
  pendingAuthStatusRequest = null;

  try {
    sessionStorage.removeItem(AUTH_STATUS_STORAGE_KEY);
  } catch (error) {
    console.warn("[auth] 清除登入狀態快取失敗", error);
  }
}

// =====================================================
// 向後端確認目前登入狀態
// -----------------------------------------------------
// 注意：這裡使用動態 import api.js，避免 api.js <-> router <-> auth.js 產生循環引用。
// =====================================================
export async function fetchAuthStatus(options = {}) {
  const { force = false } = options;

  if (!force) {
    const cached = getCachedAuthStatus();
    if (cached?.authenticated) return cached;
  }

  if (pendingAuthStatusRequest) return pendingAuthStatusRequest;

  pendingAuthStatusRequest = import("@/assets/Js/api.js")
    .then(({ default: api }) => api.get("/auth/status"))
    .then((res) => setAuthStatus(res.data || { authenticated: false }))
    .catch((error) => {
      console.warn("[auth] 檢查登入狀態失敗", error);
      return setAuthStatus({ authenticated: false });
    })
    .finally(() => {
      pendingAuthStatusRequest = null;
    });

  return pendingAuthStatusRequest;
}

// =====================================================
// 強制要求登入
// -----------------------------------------------------
// 需要登入的頁面或功能可以呼叫這個方法。
// =====================================================
export async function requireAuthStatus() {
  const status = await fetchAuthStatus({ force: true });

  if (!status.authenticated) {
    throw new Error("尚未登入或登入已過期");
  }

  return status;
}

// =====================================================
// 給舊版 parseJwt 相容使用
// -----------------------------------------------------
// 舊程式仍透過 parseJwt(token) 取得角色與登入者顯示資訊。
// 改 HttpOnly 後沒有 token 可解析，因此改回傳 /auth/status 快取。
// =====================================================
export function getCachedAuthPayload() {
  const status = getCachedAuthStatus();
  return status?.authenticated ? status : null;
}
